// OpsPulseLoader.tsx
// Next Level loader inspired by the provided neon dual-ring design.
// ✅ Build-fix version for Canvas/Preview environments:
// - Includes a DEFAULT EXPORT preview component (many sandboxes require this)
// - No Tailwind animation utilities
// - No Tailwind arbitrary value classes
// - No React.useId() (compatible with React 17+)
// - Uses plain CSS keyframes + prefers-reduced-motion
// - Unique SVG <defs> IDs via a stable instance id (no Math.random)

"use client";

import React, { useId } from "react";
import "./OpsPulseLoader.css";

type OpsPulseLoaderProps = {
  /** Visual size in pixels (width/height). */
  size?: number;
  /** Optional label shown under the loader (e.g., "Loading workspace..."). */
  label?: string;
  /** Optional sublabel for extra context. */
  sublabel?: string;
  /** Centers loader in its container with a minimum height (useful for pages). */
  fullscreen?: boolean;
  /** Use a softer glow (recommended for tables). */
  subtle?: boolean;
  /** Extra className for wrapper. */
  className?: string;
  /** Optional stable ID prefix (recommended for SSR consistency). */
  idPrefix?: string;
};

// ---------------------------------------------------------------------------
// Stable instance id helper
// - React.useId() provides SSR-safe IDs in React 18+.
// - For perfect SSR hydration consistency, pass a stable idPrefix from the parent.
// ---------------------------------------------------------------------------
function useStableInstanceId(prefix: string) {
  const reactId = useId();
  return prefix ? `${prefix}-${reactId}` : reactId;
}

/**
 * OpsPulseLoader
 * - Works in Next.js, Vite, CRA, React 17/18.
 * - Tailwind optional for layout only.
 */
export function OpsPulseLoader({
  size = 72,
  label,
  sublabel,
  fullscreen = false,
  subtle = false,
  className = "",
  idPrefix = "opsPulse",
}: OpsPulseLoaderProps) {
  const instanceId = useStableInstanceId(idPrefix);
  const gradientId = `${instanceId}-gradient`;
  const innerGradientId = `${instanceId}-innerGradient`;
  const glowId = `${instanceId}-glow`;

  const ring = Math.max(24, size);
  const strokeOuter = Math.max(6, Math.round(ring * 0.11));
  const strokeInner = Math.max(4, Math.round(ring * 0.07));

  // SVG geometry
  const cx = ring / 2;
  const cy = ring / 2;

  const rOuter = (ring - strokeOuter) / 2;
  const rInner = (ring - strokeInner) / 2 - strokeOuter * 0.25;

  const cOuter = 2 * Math.PI * rOuter;
  const cInner = 2 * Math.PI * rInner;

  // Make arcs (not full circles) to match the reference style
  const arcOuter = 0.72; // ~72% of circumference visible
  const arcInner = 0.58; // ~58% visible

  const dashOuter = `${cOuter * arcOuter} ${cOuter}`;
  const dashInner = `${cInner * arcInner} ${cInner}`;

  const aria = label ? label : "Loading";

  const Wrapper = ({ children }: { children: React.ReactNode }) => {
    if (!fullscreen) {
      return (
        <div
          className={`inline-flex flex-col items-center justify-center ${className}`.trim()}
        >
          {children}
        </div>
      );
    }

    return (
      <div
        className={`w-full flex flex-col items-center justify-center ops-pulse-fullscreen ${className}`.trim()}
      >
        {children}
      </div>
    );
  };

  // A slightly nicer label treatment without requiring Tailwind plugins
  const labelEl = (label || sublabel) ? (
    <div className="mt-4 text-center ops-pulse-label">
      {label && (
        <div className="text-sm font-medium ops-pulse-label-title">
          {label}
        </div>
      )}
      {sublabel && (
        <div className="mt-1 text-xs ops-pulse-label-subtitle">
          {sublabel}
        </div>
      )}
    </div>
  ) : null;

  return (
    <Wrapper>
      <div
        className={`relative flex items-center justify-center ops-pulse-root ${subtle ? "ops-pulse-root-subtle" : ""}`.trim()}
        aria-busy="true"
        aria-live="polite"
      >
        {/* Backdrop glow */}
        <div
          className={`absolute inset-0 rounded-full ops-pulse-backdrop ${subtle ? "ops-pulse-backdrop-subtle" : "ops-pulse-backdrop-strong"}`}
        />

        <svg
          width={ring}
          height={ring}
          viewBox={`0 0 ${ring} ${ring}`}
          className="relative"
          role="img"
          aria-label={aria}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgba(34,211,238,0.98)" />
              <stop offset="55%" stopColor="rgba(56,189,248,0.95)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0.78)" />
            </linearGradient>

            <linearGradient id={innerGradientId} x1="1" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.26)" />
              <stop offset="60%" stopColor="rgba(255,255,255,0.12)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.06)" />
            </linearGradient>

            <filter id={glowId} x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Faint base ring for depth */}
          <circle
            cx={cx}
            cy={cy}
            r={rOuter}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={Math.max(2, Math.round(strokeOuter * 0.35))}
            opacity={0.9}
          />

          {/* Outer arc */}
          <g className="ops-pulse-spin">
            <circle
              cx={cx}
              cy={cy}
              r={rOuter}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeOuter}
              strokeLinecap="round"
              strokeDasharray={dashOuter}
              strokeDashoffset={cOuter * 0.08}
              filter={`url(#${glowId})`}
              opacity={0.98}
            />
          </g>

          {/* Inner arc (counter motion) */}
          <g className="ops-pulse-spin-reverse">
            <circle
              cx={cx}
              cy={cy}
              r={rInner}
              fill="none"
              stroke={`url(#${innerGradientId})`}
              strokeWidth={strokeInner}
              strokeLinecap="round"
              strokeDasharray={dashInner}
              strokeDashoffset={cInner * 0.18}
              opacity={0.95}
            />
          </g>

          {/* Orbiting spark (small dot) */}
          <g className="ops-pulse-orbit">
            <circle
              cx={cx}
              cy={cy - rOuter}
              r={Math.max(1.5, Math.round(ring * 0.018))}
              fill="rgba(34,211,238,0.92)"
              filter={`url(#${glowId})`}
              opacity={subtle ? 0.65 : 0.95}
            />
          </g>

          {/* Core glow */}
          <circle
            cx={cx}
            cy={cy}
            r={Math.max(5, Math.round(ring * 0.12))}
            fill="rgba(34,211,238,0.10)"
            opacity={0.9}
          />
          <circle
            cx={cx}
            cy={cy}
            r={Math.max(2.5, Math.round(ring * 0.055))}
            fill="rgba(234,240,255,0.18)"
            opacity={0.9}
          />
        </svg>
      </div>

      {labelEl}

      {/* Plain CSS animations (no Tailwind config needed) */}
    </Wrapper>
  );
}

// ---------------------------------------------------------------------------
// DEFAULT EXPORT (Preview)
// Many sandbox/canvas environments expect a default export React component.
// This also gives you a visual preview immediately.
// ---------------------------------------------------------------------------
export default function OpsPulseLoaderPreview() {
  return (
    <div className="w-full flex items-center justify-center ops-pulse-preview">
      <div
        className="flex flex-col items-center ops-pulse-preview-card"
      >
        <OpsPulseLoader
          fullscreen={false}
          label="Loading workspace…"
          sublabel="Syncing live data"
          size={96}
        />
        <div className="mt-4 text-xs ops-pulse-preview-foot">
          Ops Next Level • Workforce
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Example usage in your app:
// <OpsPulseLoader fullscreen label="Loading workspace…" sublabel="Syncing live data" />
// <OpsPulseLoader size={44} subtle />
// If you have SSR hydration mismatch warnings, pass a stable idPrefix:
// <OpsPulseLoader idPrefix={`tenant-${tenantId}-loader`} />
// ---------------------------------------------------------------------------

